/**
 * Phase 15.5 P1 — Beats Marketplace Feed
 *
 * Public feed of beats with filters (BPM range, key, mood, genre,
 * license, search) and sort (featured / new / plays / price).
 * Wires into `/api/beats`. Rendered inside the existing Discovery page
 * "Beats" tab, replacing the old "Coming Soon" placeholder.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import BeatCard from "@/components/BeatCard";
import ContactProducerDialog from "@/components/ContactProducerDialog";
import ProducerLanes from "@/components/ProducerLanes";

const SORTS = [
  { id: "featured", label: "Featured" },
  { id: "new", label: "Newest" },
  { id: "plays", label: "Most played" },
  { id: "price_low", label: "Price: Low" },
  { id: "price_high", label: "Price: High" },
];

const KEYS = ["", "C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B",
              "Cm", "C#m", "Dm", "D#m", "Em", "Fm", "F#m", "Gm", "G#m", "Am", "A#m", "Bm"];

const MOODS = ["", "Dark", "Chill", "Hype", "Sad", "Romantic", "Aggressive", "Dreamy", "Uplifting"];

const LICENSES = ["", "Free", "Lease", "Premium", "Exclusive"];

const BeatsFeed = () => {
  const [beats, setBeats] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState("featured");
  const [bpmMin, setBpmMin] = useState("");
  const [bpmMax, setBpmMax] = useState("");
  const [key, setKey] = useState("");
  const [mood, setMood] = useState("");
  const [licenseType, setLicenseType] = useState("");
  const [search, setSearch] = useState("");
  const [contactBeat, setContactBeat] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort, limit: 60 };
      if (bpmMin) params.bpm_min = bpmMin;
      if (bpmMax) params.bpm_max = bpmMax;
      if (key) params.key = key;
      if (mood) params.mood = mood;
      if (licenseType) params.license_type = licenseType;
      if (search.trim()) params.search = search.trim();
      const r = await api.get("/beats", { params });
      setBeats(r.data?.beats || []);
      setTotal(r.data?.total || 0);
      setError(null);
    } catch (e) {
      setError(e?.message || "Failed to load beats");
    } finally {
      setLoading(false);
    }
  }, [sort, bpmMin, bpmMax, key, mood, licenseType, search]);

  // Refetch when non-search filters change (immediate). Search itself is
  // debounced separately below so keystrokes don't hammer the API.
  useEffect(() => { load(); }, [sort, key, mood, licenseType, bpmMin, bpmMax, load]);
  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
  }, [search, load]);

  return (
    <div data-testid="beats-feed">
      {/* Phase 19.0 — Producer Department promotion lanes (separate from artist features) */}
      <ProducerLanes />

      {/* Header / sort */}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <h2 className="text-xl font-black text-white">🎹 Beats Marketplace</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            {loading ? "Loading…" : `${total} beats available — contact producers to license, collab, or buy.`}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to="/submit-beat"
            className="px-3 py-1.5 rounded-md bg-gradient-to-r from-pink-600 to-purple-600 text-white text-xs font-black hover:opacity-90"
            data-testid="beats-feed-upload-cta"
          >
            + Upload Beat
          </Link>
          {SORTS.map((s) => (
            <button
              key={s.id}
              onClick={() => setSort(s.id)}
              data-testid={`beats-sort-${s.id}`}
              className={
                "px-2.5 py-1 rounded-md text-[11px] font-bold transition " +
                (sort === s.id
                  ? "bg-pink-500/20 text-pink-200 border border-pink-400/40"
                  : "bg-white/5 text-gray-300 border border-white/10 hover:bg-white/10")
              }
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Filters */}
      <Card className="p-3 bg-white/[0.02] border-white/10 mb-4" data-testid="beats-filters">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Search</label>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="title or tag…"
              className="mt-1 h-8 text-xs bg-white/5 border-white/15 text-white"
              data-testid="beats-search"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">BPM Min</label>
            <Input
              type="number"
              value={bpmMin}
              onChange={(e) => setBpmMin(e.target.value)}
              placeholder="60"
              min="40" max="300"
              className="mt-1 h-8 text-xs bg-white/5 border-white/15 text-white"
              data-testid="beats-bpm-min"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">BPM Max</label>
            <Input
              type="number"
              value={bpmMax}
              onChange={(e) => setBpmMax(e.target.value)}
              placeholder="200"
              min="40" max="300"
              className="mt-1 h-8 text-xs bg-white/5 border-white/15 text-white"
              data-testid="beats-bpm-max"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Key</label>
            <select
              value={key}
              onChange={(e) => setKey(e.target.value)}
              className="mt-1 w-full h-8 text-xs rounded-md bg-white/5 border border-white/15 text-white px-2"
              data-testid="beats-key"
            >
              {KEYS.map((k) => <option key={k || "any"} value={k}>{k || "Any"}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">Mood</label>
            <select
              value={mood}
              onChange={(e) => setMood(e.target.value)}
              className="mt-1 w-full h-8 text-xs rounded-md bg-white/5 border border-white/15 text-white px-2"
              data-testid="beats-mood"
            >
              {MOODS.map((m) => <option key={m || "any"} value={m}>{m || "Any"}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-gray-400 font-bold">License</label>
            <select
              value={licenseType}
              onChange={(e) => setLicenseType(e.target.value)}
              className="mt-1 w-full h-8 text-xs rounded-md bg-white/5 border border-white/15 text-white px-2"
              data-testid="beats-license"
            >
              {LICENSES.map((l) => <option key={l || "any"} value={l}>{l || "Any"}</option>)}
            </select>
          </div>
        </div>
      </Card>

      {/* Grid */}
      {error ? (
        <Card className="p-6 bg-rose-500/5 border-rose-400/30 text-rose-200 text-center" data-testid="beats-error">
          Failed to load beats. <button onClick={load} className="underline">Retry</button>
        </Card>
      ) : !loading && beats.length === 0 ? (
        <Card className="p-10 text-center bg-white/[0.02] border-white/10" data-testid="beats-empty">
          <div className="text-5xl mb-2">🎹</div>
          <h3 className="text-lg font-black text-white">No beats match your filters</h3>
          <p className="text-sm text-gray-400 mt-1 mb-4">
            Producers are uploading — clear filters or be the first to publish.
          </p>
          <Link
            to="/submit-beat"
            className="inline-block px-4 py-2 rounded-md bg-gradient-to-r from-pink-600 to-purple-600 text-white text-sm font-black hover:opacity-90"
            data-testid="beats-empty-upload-cta"
          >
            🚀 Upload your first beat
          </Link>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="beats-grid">
          {beats.map((b) => (
            <BeatCard key={b.beat_id} beat={b} onContact={setContactBeat} />
          ))}
        </div>
      )}

      <ContactProducerDialog
        open={!!contactBeat}
        onOpenChange={(o) => !o && setContactBeat(null)}
        beat={contactBeat}
      />
    </div>
  );
};

export default BeatsFeed;
